import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import npcLogService from 'src/services/apiServices/npcLogService/npcLogService';

interface NpcReviewPrintDialogProps {
  open: boolean;
  onClose: () => void;
  // Active review filters — only defined keys are forwarded so the sheet matches the dashboard.
  filters: {
    from?: string;
    to?: string;
    status?: string; // NOT_VALIDATED | APPROVED | DECLINED (omit for all)
    therapistId?: string;
  };
}

// THIN-7819: server-generated NPC review PDF preview (mirrors SchedulePrintDialog). The endpoint
// returns the pdf_file_url in response.data.data; we preview it in an iframe and offer open/print.
const NpcReviewPrintDialog: React.FC<NpcReviewPrintDialogProps> = ({ open, onClose, filters }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPdf = useCallback(() => {
    setLoading(true);
    setError(null);
    setPdfUrl(null);

    // Drop undefined/empty keys so we don't send blank query params.
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
    );

    npcLogService
      .getNpcReviewReportUrl(params)
      .then((response: any) => {
        const url = response?.data?.data;
        if (response?.data?.code === 'OK' && url) {
          setPdfUrl(url);
        } else {
          setError('Failed to generate the report.');
        }
      })
      .catch(() => setError('An error occurred while generating the report.'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    if (open) {
      fetchPdf();
    }
  }, [fetchPdf, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle>NPC Review Report</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="60vh"
            textAlign="center"
          >
            <CircularProgress />
            <Typography sx={{ mt: 2, color: '#6C737F' }}>Generating report…</Typography>
          </Box>
        ) : error ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="40vh"
            textAlign="center"
          >
            <Typography style={{ color: 'red', marginBottom: '16px' }}>{error}</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchPdf}
            >
              Retry
            </Button>
          </Box>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="NPC Review Report"
            style={{ width: '100%', height: '75vh', border: 'none' }}
          />
        ) : (
          <div>No report available.</div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: '9px', textTransform: 'none', color: '#6C737F' }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          disabled={!pdfUrl}
          onClick={() => pdfUrl && window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
          sx={{ borderRadius: '9px', textTransform: 'none', boxShadow: 'none' }}
        >
          Open in new tab
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NpcReviewPrintDialog;
